import { useState, useEffect, useCallback } from 'react';
import { savePreferencesToStorage, getPreferencesFromStorage } from './cookieUtils';

const ALL_ACCEPTED = {
  necessary: true,
  functional: true,
  analytics: true,
  performance: true,
  advertisement: true,
};

const ONLY_NECESSARY = {
  necessary: true,
  functional: false,
  analytics: false,
  performance: false,
  advertisement: false,
};

export const useCookiePreferences = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [hasAccepted, setHasAccepted] = useState(false);
  const [preferences, setPreferences] = useState(ALL_ACCEPTED);

  useEffect(() => {
    const saved = getPreferencesFromStorage();
    if (saved) {
      setHasAccepted(true);
      setPreferences(saved);
    } else {
      setShowBanner(true);
    }
  }, [setHasAccepted, setPreferences, setShowBanner]);

  const persist = useCallback((next) => {
    savePreferencesToStorage(next);
    setHasAccepted(true);
    setShowBanner(false);
  }, [setHasAccepted, setShowBanner]);

  const applyAndPersist = useCallback((next) => {
    setPreferences(next);
    persist(next);
  }, [setPreferences, persist]);

  const acceptAll = useCallback(() => applyAndPersist(ALL_ACCEPTED), [applyAndPersist]);
  const rejectAll = useCallback(() => applyAndPersist(ONLY_NECESSARY), [applyAndPersist]);
  const savePreferences = useCallback((next) => persist(next || preferences), [persist, preferences]);

  const togglePreference = useCallback((key) => {
    if (key === 'necessary') return;
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  }, [setPreferences]);

  return {
    showBanner,
    hasAccepted,
    preferences,
    acceptAll,
    rejectAll,
    savePreferences,
    togglePreference,
  };
};
